// src/components/Faq/FaqView.jsx
import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, HelpCircle } from "lucide-react";
import { useClientConfig, useReports, useTierConfig } from "../../hooks";
import { loadMarkdownContent } from "../../services/firebase";
import { marked } from "marked";

// Configure marked for clean output
marked.setOptions({
  gfm: true,
  breaks: true,
});

// ============================================
// TIER COLOR BADGE INJECTION
// ============================================

/**
 * Default tier color map — used if config colors aren't available.
 * Matches the CSS variables in index.css.
 */
const DEFAULT_TIER_COLORS = {
  ideal: { bg: "#4caf50", text: "#fff" },
  strong: { bg: "#66bb6a", text: "#fff" },
  "high mid": { bg: "#ffb74d", text: "#4a3000" },
  "low mid": { bg: "#ff8a65", text: "#4a1500" },
  no: { bg: "#f44336", text: "#fff" },
  poor: { bg: "#f44336", text: "#fff" },
  "no data": { bg: "#9e9e9e", text: "#fff" },
};

/**
 * Tier name patterns to search for in FAQ HTML.
 * Ordered longest-first so "High Mid" matches before "High".
 * Each entry: [display name, normalized key, optional regex for parenthetical color hint]
 */
const TIER_PATTERNS = [
  { name: "High Mid", key: "high mid" },
  { name: "Low Mid", key: "low mid" },
  { name: "Ideal", key: "ideal" },
  { name: "Strong", key: "strong" },
  { name: "No Data", key: "no data" },
  { name: "Poor", key: "poor" },
];

/**
 * Build a colored badge span for a tier name.
 * Uses a subtle tinted background with the tier color and rounded corners.
 */
const buildBadge = (matchedText, tierKey) => {
  const colors = DEFAULT_TIER_COLORS[tierKey];
  if (!colors) return matchedText;

  // Softer style: tinted background at 18% opacity, colored text, rounded pill
  return `<span style="
    display: inline-block;
    background-color: ${colors.bg}22;
    color: ${colors.bg};
    font-weight: 600;
    padding: 1px 8px;
    border-radius: 4px;
    border: 1px solid ${colors.bg}44;
    white-space: nowrap;
  ">${matchedText}</span>`;
};

/**
 * Post-process rendered HTML to inject tier-colored badges,
 * scoped to ONLY the "What do the colors mean?" section (Q11).
 *
 * Finds the Q11 heading, extracts the content up to the next heading,
 * applies badge styling within that slice, then reassembles.
 */
const injectTierBadges = (html) => {
  // Find the Q11 heading — marked renders ### as <h3>
  const q11Start = html.search(
    /<h3[^>]*>[^<]*What do the colors mean\?[^<]*<\/h3>/i,
  );
  if (q11Start === -1) return html; // Q11 not found, return as-is

  // Find the end of the Q11 <h3> tag, then the next <h2> or <h3> after it
  const afterHeading = html.indexOf("</h3>", q11Start) + 5;
  const nextHeadingMatch = html.slice(afterHeading).search(/<h[23][^>]*>/i);
  const q11End =
    nextHeadingMatch === -1
      ? html.length // Q11 is the last section
      : afterHeading + nextHeadingMatch;

  const before = html.slice(0, afterHeading);
  let section = html.slice(afterHeading, q11End);
  const after = html.slice(q11End);

  // Apply badge replacements only within the Q11 section
  for (const { name, key } of TIER_PATTERNS) {
    const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    const pattern = new RegExp(
      `(?<![\\w-])` +
        `(${escapedName}` +
        `(?:\\s*\\([^)]*\\))?)` +
        `(?=[\\s,;:.!?<(]|$)`,
      "gi",
    );

    let replaced = false;
    section = section.replace(pattern, (match) => {
      if (replaced) return match;
      replaced = true;
      return buildBadge(match, key);
    });
  }

  return before + section + after;
};

// ============================================
// SCOPED STYLES
// ============================================

const faqStyles = `
  .faq-content {
    color: #374151;
    line-height: 1.75;
    font-size: 0.9375rem;
  }

  @media (min-width: 1024px) {
    .faq-content {
      font-size: 1rem;
    }
  }

  .faq-content h1 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
    margin-top: 0;
    margin-bottom: 0.75rem;
    padding-bottom: 0.75rem;
    border-bottom: 2px solid #e5e7eb;
  }

  @media (min-width: 1024px) {
    .faq-content h1 {
      font-size: 1.875rem;
      margin-bottom: 1rem;
    }
  }

  .faq-content h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
    margin-top: 1.75rem;
    margin-bottom: 0.75rem;
  }

  @media (min-width: 1024px) {
    .faq-content h2 {
      font-size: 1.375rem;
      margin-top: 2rem;
    }
  }

  .faq-content h3 {
    font-size: 1.0625rem;
    font-weight: 600;
    color: #374151;
    margin-top: 1.25rem;
    margin-bottom: 0.5rem;
  }

  @media (min-width: 1024px) {
    .faq-content h3 {
      font-size: 1.125rem;
      margin-top: 1.5rem;
    }
  }

  .faq-content p {
    margin-bottom: 1rem;
  }

  .faq-content ul,
  .faq-content ol {
    margin-bottom: 1rem;
    padding-left: 1.25rem;
  }

  @media (min-width: 1024px) {
    .faq-content ul,
    .faq-content ol {
      padding-left: 1.5rem;
    }
  }

  .faq-content ul {
    list-style-type: disc;
  }

  .faq-content ol {
    list-style-type: decimal;
  }

  .faq-content li {
    margin-bottom: 0.375rem;
  }

  .faq-content li > ul,
  .faq-content li > ol {
    margin-top: 0.375rem;
    margin-bottom: 0;
  }

  .faq-content a {
    color: #15803d;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .faq-content a:hover {
    color: #166534;
  }

  .faq-content strong {
    font-weight: 600;
    color: #1f2937;
  }

  .faq-content em {
    font-style: italic;
  }

  .faq-content blockquote {
    border-left: 4px solid #15803d;
    padding: 0.75rem 1rem;
    margin: 1rem 0;
    background-color: #f0fdf4;
    color: #4b5563;
    border-radius: 0 0.375rem 0.375rem 0;
  }

  .faq-content blockquote p:last-child {
    margin-bottom: 0;
  }

  .faq-content code {
    background-color: #f3f4f6;
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
    font-size: 0.875em;
    font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace;
  }

  .faq-content pre {
    background-color: #1f2937;
    color: #e5e7eb;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    overflow-x: auto;
    margin-bottom: 1rem;
    font-size: 0.8125rem;
  }

  @media (min-width: 1024px) {
    .faq-content pre {
      padding: 1rem;
      font-size: 0.875rem;
    }
  }

  .faq-content pre code {
    background: none;
    padding: 0;
    color: inherit;
  }

  .faq-content hr {
    border: none;
    border-top: 1px solid #e5e7eb;
    margin: 1.5rem 0;
  }

  @media (min-width: 1024px) {
    .faq-content hr {
      margin: 2rem 0;
    }
  }

  .faq-content table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 1rem;
    font-size: 0.875rem;
  }

  .faq-content th,
  .faq-content td {
    border: 1px solid #e5e7eb;
    padding: 0.375rem 0.625rem;
    text-align: left;
  }

  @media (min-width: 1024px) {
    .faq-content th,
    .faq-content td {
      padding: 0.5rem 0.75rem;
    }
  }

  .faq-content th {
    background-color: #f9fafb;
    font-weight: 600;
    color: #1f2937;
  }

  .faq-content img {
    max-width: 100%;
    border-radius: 0.5rem;
    margin: 1rem 0;
  }
`;

// ============================================
// FAQ VIEW COMPONENT
// ============================================

const FaqView = () => {
  const { clientSlug } = useParams();
  const { clientInfo } = useClientConfig();
  const { globalReports } = useReports();

  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqReport = globalReports.find(
    (r) => r.type === "markdown" || r.id === "faq",
  );

  useEffect(() => {
    if (!faqReport?.storagePath) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchFaq = async () => {
      try {
        const md = await loadMarkdownContent(clientSlug, faqReport.storagePath);
        if (cancelled) return;

        // Parse markdown → HTML, then inject tier color badges
        let html = marked.parse(md);
        html = injectTierBadges(html);

        setContent(html);
      } catch (err) {
        if (cancelled) return;
        console.error("Error loading FAQ:", err);
        setError("Failed to load FAQ content. Please try again later.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchFaq();
    return () => {
      cancelled = true;
    };
  }, [clientSlug, faqReport]);

  return (
    <div className="min-h-screen bg-gray-50">
      <style>{faqStyles}</style>

      {/* Header */}
      <header className="bg-primary text-white shadow-md">
        <div className="max-w-4xl mx-auto px-4 py-3 lg:py-4">
          <div className="flex items-center gap-3 lg:gap-4">
            <Link
              to={`/${clientSlug}`}
              className="flex items-center gap-1.5 lg:gap-2 text-gray-300 hover:text-white transition"
            >
              <ArrowLeft size={18} />
              <span className="text-sm hidden sm:inline">Back to Map</span>
            </Link>
            <div className="h-5 lg:h-6 w-px bg-gray-600" />
            <h1 className="text-lg lg:text-xl font-bold">FAQs</h1>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-3 lg:px-4 py-6 lg:py-8">
        {loading ? (
          <div className="bg-white rounded-xl shadow-sm p-8 flex items-center justify-center min-h-[200px]">
            <div className="flex items-center gap-3 text-gray-500">
              <div className="w-5 h-5 border-2 border-gray-300 border-t-secondary rounded-full animate-spin" />
              <span>Loading FAQs...</span>
            </div>
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <HelpCircle size={48} className="mx-auto text-red-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              Error Loading FAQs
            </h3>
            <p className="text-gray-500 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary-dark transition text-sm"
            >
              Retry
            </button>
          </div>
        ) : content ? (
          <div className="bg-white rounded-xl shadow-sm p-5 lg:p-8">
            <div
              className="faq-content"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <HelpCircle size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              No FAQs Available
            </h3>
            <p className="text-gray-500">
              FAQs for this project are not yet available. Check back later.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default FaqView;
